import { Box, Code, Group, Badge as MantineBadge, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";

interface PropertiesProps {
  children?: ReactNode;
  domAttributes?: string;
}

/**
 * Properties renders the {% properties %} tag as a bordered, stacked list of
 * {% property %} rows describing an API object's fields.
 *
 * `domAttributes` names the JSX intrinsic an element also spreads. Those attributes are not
 * listed as rows on purpose — `<Glyph>` accepts 362 of them, and burying `name` and `label`
 * under `onAnimationIteration` documents React rather than this API. One line says the same
 * thing and points somewhere better.
 */
export const Properties = ({ children, domAttributes }: PropertiesProps) => (
  // mantine:exempt — top divider rule on the field list; Mantine has no border-side shortcut prop.
  <Stack
    gap={0}
    my="md"
    style={{
      borderTop: "1px solid var(--mantine-color-default-border)",
    }}
  >
    {children}
    {domAttributes ? (
      <Text c="dimmed" py="sm" size="xs">
        Also accepts every standard <Code>{`<${domAttributes}>`}</Code> attribute — a ref, event
        handlers, <Code>data-*</Code> and ARIA — which are forwarded to the rendered element.
      </Text>
    ) : null}
  </Stack>
);

interface PropertyProps {
  children?: ReactNode;
  default?: string;
  name?: string;
  required?: boolean;
  type?: string;
}

/**
 * Property renders one {% property %} as a field row: the `name` in `Code`, the
 * `type` muted, a "required" badge when set, the `default` when present, and the
 * description below.
 */
export const Property = ({
  children,
  default: defaultValue,
  name,
  required,
  type,
}: PropertyProps) => (
  // mantine:exempt — per-row bottom divider; Mantine has no border-side shortcut prop.
  <Box
    py="sm"
    style={{
      borderBottom: "1px solid var(--mantine-color-default-border)",
    }}
  >
    <Group>
      <Code>{name}</Code>
      {type ? (
        <Text c="dimmed" size="xs">
          {type}
        </Text>
      ) : null}
      {required ? <MantineBadge color="red">required</MantineBadge> : null}
      {defaultValue ? (
        <Text c="dimmed" size="xs">
          default: <Code>{defaultValue}</Code>
        </Text>
      ) : null}
    </Group>
    {children ? (
      <Text c="dimmed" component="div" mt="xxs">
        {children}
      </Text>
    ) : null}
  </Box>
);
